import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import api, { setDevToken } from '../services/api';

interface Operator {
  sub: string;
  email: string;
  name: string;
  roles: string[];
  readOnly: boolean;
}

interface AuthCtx {
  operator: Operator | null;
  loading: boolean;
  signIn: () => void;
  signOut: () => void;
}

const Ctx = createContext<AuthCtx>({ operator: null, loading: true, signIn: () => {}, signOut: () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [operator, setOperator] = useState<Operator | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Dev fallback: a ?token= injected by a local OIDC proxy is kept in
    // memory only (never Web Storage) and sent as a bearer by the api client. In production, auth rides on the
    // oauth2-proxy cookie + ForwardAuth (which injects the bearer on /api/*),
    // so no browser-readable token exists.
    const params = new URLSearchParams(window.location.search);
    const devToken = params.get('token');
    if (devToken) {
      setDevToken(devToken);
      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url);
    }

    // Identity comes from the backend: /api/me reflects the validated JWT.
    api.get<Operator>('/me')
      .then((r) => setOperator(r.data))
      .catch(() => setOperator(null))
      .finally(() => setLoading(false));
  }, []);

  const signIn = () => {
    // Full-page redirect into the oauth2-proxy login. On return the SPA
    // reloads, /api/me succeeds via the freshly-set cookie, and operator
    // is populated.
    window.location.href = '/oauth2/sign_in?rd=' + encodeURIComponent(window.location.pathname);
  };

  const signOut = () => {
    setDevToken(null);
    setOperator(null);
    window.location.href = '/oauth2/sign_out';
  };

  return <Ctx.Provider value={{ operator, loading, signIn, signOut }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
